import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Layers } from "lucide-react";
import { fmtNumber } from "./shared";

// Canonical floor ordering. Anything not in this list is appended at the end.
const FLOOR_ORDER = [
  "Basement", "Ground", "First", "Second", "Third", "Fourth", "Fifth",
  "Sixth", "Seventh", "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth",
  "Thirteenth", "Fourteenth", "Fifteenth", "Sixteenth", "Seventeenth",
  "Eighteenth", "Nineteenth", "Twentieth",
];
function floorRank(label) {
  const idx = FLOOR_ORDER.indexOf((label || "").trim());
  return idx === -1 ? 999 : idx;
}

// Group a property's units by floor → returns array of synthetic floor nodes.
function buildFloorNodes(property) {
  const groups = {};
  for (const u of property.units || []) {
    const k = (u.floor || "").trim() || "Unassigned";
    (groups[k] = groups[k] || []).push(u);
  }
  return Object.entries(groups)
    .sort((a, b) => floorRank(a[0]) - floorRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([floor, units]) => ({
      id: `${property.id}__floor__${floor}`,
      name: floor === "Unassigned" ? "Unassigned Floor" : `${floor} Floor`,
      is_floor_group: true,
      unit_count: units.length,
      units: [...units].sort((a, b) =>
        (a.assigned_room_no || a.name).localeCompare(b.assigned_room_no || b.name),
      ),
    }));
}

// Flatten the tree into a list of rows so we can render iteratively (avoids
// recursive JSX components that confuse Babel's traversal on hot-reload).
function flatten(tree, expanded) {
  const rows = [];
  const walk = (node, level, kind, parentChain) => {
    const expandKey = `${kind}:${node.id}`;
    let children = [];
    if (kind === "property") {
      const floorNodes = buildFloorNodes(node);
      children = floorNodes.map((f) => ({ node: f, kind: "floor" }));
    } else if (kind === "floor") {
      children = (node.units || []).map((u) => ({ node: u, kind: "unit" }));
    } else {
      children = (node.properties || []).map((p) => ({ node: p, kind: "property" }));
    }
    const hasChildren = children.length > 0;
    const isOpen = hasChildren && expanded.has(expandKey);
    rows.push({ node, level, kind, hasChildren, isOpen, expandKey, parentChain });
    if (isOpen) {
      for (const c of children) {
        walk(c.node, level + 1, c.kind, [...parentChain, expandKey]);
      }
    }
  };
  for (const land of tree) {
    walk(land, 0, "land", []);
  }
  return rows;
}

export default function TreeTab({ tree }) {
  // Default: lands open, properties open, floors open
  const initialExpanded = useMemo(() => {
    const set = new Set();
    for (const land of tree) {
      set.add(`land:${land.id}`);
      for (const p of land.properties || []) {
        set.add(`property:${p.id}`);
        for (const f of buildFloorNodes(p)) {
          set.add(`floor:${f.id}`);
        }
      }
    }
    return set;
  }, [tree]);
  const [expanded, setExpanded] = useState(initialExpanded);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const rows = useMemo(() => flatten(tree, expanded), [tree, expanded]);

  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm p-6" data-testid="tree-tab">
      {tree.length === 0 ? (
        <div className="text-sm text-[#6b655b] italic">No properties to show.</div>
      ) : (
        <div>
          <div className="font-serif text-xl text-[#1c1a17] mb-2">GS Mandal</div>
          {rows.map((r) => {
            const n = r.node;
            const isFloor = r.kind === "floor";
            const isUnit = r.kind === "unit";
            const childArea = n.built_up_area || n.area;
            const subtitle = isFloor
              ? `${n.unit_count} unit${n.unit_count === 1 ? "" : "s"}`
              : (n.property_type ||
                n.unit_type ||
                (n.area_unit ? `${fmtNumber(n.total_area)} ${n.area_unit}` : ""));
            return (
              <div
                key={`${r.kind}-${n.id}`}
                data-testid={`tree-node-${n.id}`}
                className={r.level === 0 ? "border-t border-[#e5e0d8] py-2" : "py-1"}
                style={{ paddingLeft: r.level * 20 }}
              >
                <div
                  className={`flex items-center gap-2 ${r.hasChildren ? "cursor-pointer" : ""}`}
                  onClick={() => r.hasChildren && toggle(r.expandKey)}
                >
                  {r.hasChildren ? (
                    r.isOpen ? (
                      <ChevronDown size={14} strokeWidth={1.5} className="text-[#234333]" />
                    ) : (
                      <ChevronRight size={14} strokeWidth={1.5} className="text-[#234333]" />
                    )
                  ) : (
                    <div className="w-3.5" />
                  )}
                  {isFloor ? (
                    <Layers size={13} strokeWidth={1.5} className="text-[#8a6d1d]" />
                  ) : null}
                  <span
                    className={
                      isFloor
                        ? "text-[11px] tracking-[0.15em] uppercase text-[#8a6d1d] font-semibold"
                        : "text-sm text-[#1c1a17]"
                    }
                  >
                    {n.name}
                  </span>
                  {isUnit && n.assigned_room_no ? (
                    <span
                      data-testid={`tree-unit-room-${n.id}`}
                      className="text-[10px] font-mono tracking-wide uppercase border border-[#234333]/30 text-[#234333] bg-[#234333]/10 px-1.5 py-0.5 rounded-sm"
                    >
                      {n.assigned_room_no}
                    </span>
                  ) : null}
                  {subtitle ? (
                    <span className="text-[10px] tracking-wide uppercase text-[#6b655b]">
                      {subtitle}
                    </span>
                  ) : null}
                  {!isFloor && childArea && childArea > 0 ? (
                    <span className="text-[11px] text-[#6b655b]">
                      · {fmtNumber(childArea)} sqft
                    </span>
                  ) : null}
                  {n.is_occupied != null && !isFloor ? (
                    <span
                      className={`ml-2 inline-block px-1.5 py-0.5 text-[9px] tracking-wide uppercase border rounded-sm ${
                        n.is_occupied
                          ? "bg-[#234333]/10 text-[#234333] border-[#234333]/30"
                          : "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/30"
                      }`}
                    >
                      {n.is_occupied ? "Occupied" : "Vacant"}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
